import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Check } from "lucide-react";

import PageShell from "@/components/site/PageShell";

const initial = { name: "", email: "", company: "", message: "" };

export default function Demo() {
    const [form, setForm] = useState(initial);
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const update = (field) => (e) =>
        setForm((f) => ({ ...f, [field]: e.target.value }));

    const onSubmit = (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Name and email are required.");
            return;
        }
        setSubmitting(true);
        // Phase 1: no backend submission; simulate brief delay.
        setTimeout(() => {
            // eslint-disable-next-line no-console
            console.log("[Concrete Control] Demo request submitted:", form);
            setSubmitting(false);
            setSubmitted(true);
            toast.success("Request received. We'll be in touch.");
        }, 350);
    };

    return (
        <PageShell testid="demo-page">
            <section className="border-b border-[#d8dcdd]">
                <div className="mx-auto max-w-[1280px] px-6 lg:px-10 py-20 lg:py-24">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                        {/* Left: context */}
                        <div className="lg:col-span-5 flex flex-col gap-6">
                            <span className="tc-eyebrow text-[#01696f]">
                                Request demo
                            </span>
                            <h1 className="text-[30px] sm:text-[36px] lg:text-[40px] font-medium tracking-[-0.02em] text-[#0e1416] leading-[1.1]">
                                See Concrete Control on a pour from your project.
                            </h1>
                            <p className="text-[15px] text-[#5a6669] leading-[1.65] max-w-[44ch]">
                                Share a brief context — geometry, mix, target
                                pour date, maturity method preference (Nurse-Saul
                                or Arrhenius) — and we&apos;ll set up a working
                                session with a Concrete Control engineer.
                                Demos run 30–45 minutes.
                            </p>

                            <ul className="mt-2 flex flex-col gap-2.5">
                                {[
                                    "Walkthrough of the connected workflow (Plan → Cure → Next pour)",
                                    "Mix calibration approach (lab points or field validation)",
                                    "Review of ΔT, CIF, and in-place f_c outputs",
                                    "CIC-alignment Q&A for your project context",
                                ].map((item) => (
                                    <li
                                        key={item}
                                        className="flex items-start gap-2.5 text-[13.5px] text-[#0e1416]"
                                    >
                                        <Check
                                            size={14}
                                            strokeWidth={1.5}
                                            className="mt-[3px] text-[#01696f] shrink-0"
                                        />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-6 pt-5 border-t border-[#d8dcdd] tc-mono text-[11px] text-[#5a6669]">
                                Or email us directly — engineering@concrete-control.com
                            </div>
                        </div>

                        {/* Right: form */}
                        <div className="lg:col-span-7">
                            <div
                                data-testid="demo-form-frame"
                                className="bg-[#ffffff] border border-[#d8dcdd] p-7 lg:p-10"
                            >
                                {!submitted ? (
                                    <form
                                        data-testid="demo-form"
                                        onSubmit={onSubmit}
                                        className="flex flex-col gap-6"
                                        noValidate
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="tc-eyebrow text-[#5a6669]">
                                                Demo request form
                                            </span>
                                            <span className="tc-mono text-[10.5px] text-[#5a6669]">
                                                F.01
                                            </span>
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                            <Field
                                                id="name"
                                                label="Name"
                                                placeholder="Jane Engineer"
                                                value={form.name}
                                                onChange={update("name")}
                                                required
                                                testid="demo-input-name"
                                            />
                                            <Field
                                                id="email"
                                                type="email"
                                                label="Work email"
                                                placeholder="jane@firm.com"
                                                value={form.email}
                                                onChange={update("email")}
                                                required
                                                testid="demo-input-email"
                                            />
                                        </div>

                                        <Field
                                            id="company"
                                            label="Company"
                                            placeholder="Firm or contractor"
                                            value={form.company}
                                            onChange={update("company")}
                                            testid="demo-input-company"
                                        />

                                        <FieldArea
                                            id="message"
                                            label="Project context"
                                            placeholder="Geometry, mix, target pour date, anything relevant…"
                                            value={form.message}
                                            onChange={update("message")}
                                            testid="demo-input-message"
                                        />

                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2 border-t border-[#d8dcdd]">
                                            <p className="tc-mono text-[11px] text-[#5a6669]">
                                                We respond within 1 business
                                                day.
                                            </p>
                                            <button
                                                type="submit"
                                                data-testid="demo-submit-button"
                                                disabled={submitting}
                                                className="group inline-flex items-center justify-between gap-3 bg-[#01696f] hover:bg-[#015a60] disabled:opacity-60 disabled:cursor-not-allowed text-[#ffffff] px-5 py-3 text-[13.5px] font-medium transition-colors duration-200"
                                            >
                                                <span>
                                                    {submitting
                                                        ? "Sending…"
                                                        : "Send request"}
                                                </span>
                                                <ArrowUpRight
                                                    size={14}
                                                    strokeWidth={1.5}
                                                    className="transition-transform duration-200 group-hover:translate-x-[1px] group-hover:-translate-y-[1px]"
                                                />
                                            </button>
                                        </div>
                                    </form>
                                ) : (
                                    <div
                                        data-testid="demo-success-state"
                                        className="flex flex-col items-start gap-5"
                                    >
                                        <div className="inline-flex items-center justify-center w-9 h-9 border border-[#01696f] text-[#01696f]">
                                            <Check
                                                size={16}
                                                strokeWidth={1.5}
                                            />
                                        </div>
                                        <h2 className="text-[22px] font-medium tracking-tight text-[#0e1416]">
                                            Request received.
                                        </h2>
                                        <p className="text-[14px] text-[#5a6669] leading-[1.6] max-w-[48ch]">
                                            Thanks{form.name ? `, ${form.name}` : ""}.
                                            A Concrete Control engineer will
                                            reach out within one business day to
                                            schedule your session.
                                        </p>
                                        <button
                                            type="button"
                                            data-testid="demo-success-reset"
                                            onClick={() => {
                                                setForm(initial);
                                                setSubmitted(false);
                                            }}
                                            className="mt-2 inline-flex items-center gap-2 text-[13px] text-[#01696f] hover:text-[#015a60] transition-colors"
                                        >
                                            Send another request
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </PageShell>
    );
}

/* ---------- form atoms ---------- */

const Field = ({
    id,
    label,
    placeholder,
    value,
    onChange,
    type = "text",
    required = false,
    testid,
}) => (
    <div className="flex flex-col gap-2">
        <label
            htmlFor={id}
            className="tc-mono text-[10.5px] text-[#5a6669] tracking-wider uppercase"
        >
            {label}
            {required && <span className="text-[#01696f]"> *</span>}
        </label>
        <input
            id={id}
            type={type}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            data-testid={testid}
            required={required}
            className="w-full bg-transparent border-0 border-b border-[#d8dcdd] focus:border-[#01696f] outline-none px-0 py-2 text-[14px] text-[#0e1416] placeholder:text-[#8a9498] transition-colors"
        />
    </div>
);

const FieldArea = ({ id, label, placeholder, value, onChange, testid }) => (
    <div className="flex flex-col gap-2">
        <label
            htmlFor={id}
            className="tc-mono text-[10.5px] text-[#5a6669] tracking-wider uppercase"
        >
            {label}
        </label>
        <textarea
            id={id}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            data-testid={testid}
            rows={4}
            className="w-full bg-transparent border border-[#d8dcdd] focus:border-[#01696f] outline-none px-3 py-2.5 text-[14px] text-[#0e1416] placeholder:text-[#8a9498] transition-colors resize-none leading-[1.55]"
        />
    </div>
);
